import { useParams, Link } from 'react-router-dom'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import type { OrderStatus } from '@webmono/storefront-ui/types'
import { formatDateTime, cn } from '@webmono/storefront-ui/utils'
import { useOrder } from '@/lib/hooks'

const STATUS_STEPS: { status: OrderStatus; label: string }[] = [
  { status: 'confirmed', label: 'Order Confirmed' },
  { status: 'preparing', label: 'Preparing' },
  { status: 'ready', label: 'Ready' },
]

const DELIVERY_STATUS_STEPS: { status: OrderStatus; label: string }[] = [
  { status: 'confirmed', label: 'Order Confirmed' },
  { status: 'preparing', label: 'Preparing' },
  { status: 'out_for_delivery', label: 'Out for Delivery' },
  { status: 'delivered', label: 'Delivered' },
]

function getStatusIndex(status: OrderStatus, isDelivery: boolean): number {
  const steps = isDelivery ? DELIVERY_STATUS_STEPS : STATUS_STEPS
  const index = steps.findIndex((s) => s.status === status)
  if (index >= 0) return index
  if (status === 'picked_up') return STATUS_STEPS.length
  return 0
}

export function OrderTrackingPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const tenant = useTenantConfig()
  const { data: order, isLoading } = useOrder(orderId!)

  if (isLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
          <div className="h-4 bg-muted rounded w-2/3 mx-auto" />
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h1 className="text-xl font-heading font-semibold text-foreground mb-2">Order Not Found</h1>
        <Link
          to="/menu"
          className="inline-flex bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium"
        >
          Return to Menu
        </Link>
      </div>
    )
  }

  const isDelivery = order.fulfillmentType === 'delivery'
  const steps = isDelivery ? DELIVERY_STATUS_STEPS : STATUS_STEPS
  const currentIndex = getStatusIndex(order.status, isDelivery)
  const isComplete = order.status === 'picked_up' || order.status === 'delivered'
  const isCancelled = order.status === 'cancelled'

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <Link
        to={`/order/${order.id}`}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Order Details
      </Link>

      <div className="text-center mb-8">
        <p className="text-sm text-muted-foreground mb-1">Order #{order.orderNumber}</p>
        <h1 className="text-xl font-heading font-semibold text-foreground">
          {isCancelled && 'Order Cancelled'}
          {isComplete && (isDelivery ? 'Delivered!' : 'Picked Up!')}
          {!isCancelled && !isComplete && 'Tracking Your Order'}
        </h1>
      </div>

      {isCancelled ? (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg text-center mb-6">
          This order has been cancelled.
        </div>
      ) : (
        <div className="mb-8">
          <div className="relative">
            {steps.map((step, index) => {
              const isActive = index === currentIndex
              const isCompleted = index < currentIndex || isComplete

              return (
                <div key={step.status} className="flex items-start gap-4 mb-6 last:mb-0">
                  <div className="relative flex flex-col items-center">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center border-2 z-10',
                        isCompleted && 'bg-success border-success',
                        isActive && !isCompleted && 'bg-primary border-primary animate-pulse',
                        !isCompleted && !isActive && 'bg-muted border-border'
                      )}
                    >
                      {isCompleted ? (
                        <svg className="w-4 h-4 text-success-foreground" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className={cn(
                          'text-sm font-medium',
                          isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                        )}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={cn(
                          'absolute top-8 w-0.5 h-8',
                          index < currentIndex ? 'bg-success' : 'bg-border'
                        )}
                      />
                    )}
                  </div>
                  <div className="pt-1">
                    <p className={cn(
                      'font-medium',
                      isCompleted || isActive ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {step.label}
                    </p>
                    {isActive && order.estimatedReadyTime && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Est. {formatDateTime(order.estimatedReadyTime)}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-4">
        <h3 className="font-medium text-foreground mb-3">
          {order.fulfillmentType === 'pickup' && 'Pickup Location'}
          {order.fulfillmentType === 'delivery' && 'Delivery Address'}
          {order.fulfillmentType === 'table_service' && 'Table Service'}
        </h3>

        {order.fulfillmentType === 'pickup' && (
          <div className="text-sm space-y-1">
            <p className="font-medium text-foreground">{tenant.name}</p>
            <p className="text-muted-foreground">{tenant.contact.address}</p>
            <p className="text-muted-foreground">
              {tenant.contact.city}, {tenant.contact.state} {tenant.contact.zip}
            </p>
            <a
              href={`tel:${tenant.contact.phone}`}
              className="text-primary hover:underline block mt-2"
            >
              {tenant.contact.phone}
            </a>
          </div>
        )}

        {order.fulfillmentType === 'delivery' && order.deliveryAddress && (
          <div className="text-sm space-y-1">
            <p className="text-foreground">{order.deliveryAddress.street}</p>
            {order.deliveryAddress.unit && (
              <p className="text-foreground">Unit {order.deliveryAddress.unit}</p>
            )}
            <p className="text-muted-foreground">
              {order.deliveryAddress.city}, {order.deliveryAddress.province} {order.deliveryAddress.postalCode}
            </p>
          </div>
        )}

        {order.fulfillmentType === 'table_service' && (
          <p className="text-sm text-foreground">Table {order.tableNumber}</p>
        )}
      </div>

      <div className="mt-6">
        <Link
          to="/menu"
          className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-medium text-center hover:bg-secondary/80 transition-colors"
        >
          Order Again
        </Link>
      </div>
    </div>
  )
}
